/**
 * useAddToStasht - Hook for adding a post to user's stash
 *
 * Manages the save operation state machine:
 *   idle → saving → success → idle
 *
 * Returns `hasSaved` to track if we saved this session, which persists
 * even after state returns to idle. This is needed because query
 * invalidation may not complete before the success state dismisses.
 */

import { createElement, useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { postsService } from "@/services/posts.service";
import { stashListQueryKey } from "@/features/stash/stashQueryKeys";
import { addRecentStashId, removeRecentStashId } from "@/hooks/useRecentStashes";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import type { FeedPost } from "@/features/stash/feedPost";

/** How long to show "Added" success state before dismissing */
const SUCCESS_DISPLAY_MS = 3000;

export type AddToStashtState = "idle" | "saving" | "success";

export interface UseAddToStashtOptions {
  postId: string;
  /** Whether the post is already saved (prevents duplicate saves) */
  isSaved?: boolean;
  origin?: "discover" | "public_search";
}

export interface UseAddToStashtResult {
  state: AddToStashtState;
  save: () => Promise<void>;
  /** True if we successfully saved this session (persists after state → idle) */
  hasSaved: boolean;
}

export function useAddToStasht({
  postId,
  isSaved = false,
  origin,
}: UseAddToStashtOptions): UseAddToStashtResult {
  const [state, setState] = useState<AddToStashtState>("idle");
  const [hasSaved, setHasSaved] = useState(false);
  const mountedRef = useRef(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const account = useRef(user?.user_id);
  useLayoutEffect(() => { account.current = user?.user_id; }, [user?.user_id]);

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const save = useCallback(async () => {
    // Guard: don't save if already saved, or operation in progress
    if (!postId || isSaved || hasSaved || state !== "idle") {
      return;
    }

    setState("saving");
    const owner = account.current;

    try {
      await postsService.savePost(postId, origin, () => account.current === owner);

      if (account.current !== owner) return;
      const key = stashListQueryKey(postId);
      const title = queryClient.getQueryData<FeedPost>(key)?.title || "Stash";
      queryClient.setQueryData<FeedPost>(key, current => current && { ...current, user_state: "active" });
      addRecentStashId(queryClient, postId);

      if (origin) toast({
        title: "Added to your stashes",
        description: title,
        action: createElement(ToastAction, {
          altText: `Undo saving ${title}`,
          onClick: async () => {
            if (account.current !== owner) return;
            try {
              await postsService.deletePost(postId, false, () => account.current === owner);
              if (account.current !== owner) return;
              removeRecentStashId(queryClient, postId);
              queryClient.setQueryData<FeedPost>(key, current => current && { ...current, user_state: "removed" });
              if (mountedRef.current) { setHasSaved(false); setState("idle"); }
              void queryClient.invalidateQueries({ queryKey: ["stashes"] });
              void queryClient.invalidateQueries({ queryKey: key });
            } catch {
              if (account.current !== owner) return;
              toast({ title: "Couldn't undo save", description: "Please try again.", variant: "destructive" });
            }
          },
        }, "Undo"),
      });

      if (!mountedRef.current) return;

      setState("success");
      setHasSaved(true);

      // Start dismiss timer immediately (don't wait for query invalidation)
      setTimeout(() => {
        if (mountedRef.current) {
          setState("idle");
        }
      }, SUCCESS_DISPLAY_MS);

      // The save is durable before this runs; refresh the list and cached item
      // so later server reads confirm the same state.
      void (async () => {
        await queryClient.invalidateQueries({ queryKey: ["stashes"] });
        await queryClient.invalidateQueries({ queryKey: stashListQueryKey(postId) });
        await queryClient.invalidateQueries({ queryKey: ["entity"] });
      })();
    } catch (error) {
      if (!mountedRef.current || account.current !== owner) return;

      console.error("Failed to save post:", error);
      setState("idle");
      toast({
        title: "Failed to add to stash",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  }, [postId, isSaved, hasSaved, state, toast, queryClient, origin]);

  return { state, save, hasSaved };
}
